// GrowthBook bridge — initializes the SDK on DOMContentLoaded and resolves window.gbReady.
// Consumers should read flags via window.gbCore.getFeatureValue(key, default), NOT directly via gb,
// so the call site is the single trigger point for the `Experiment Viewed` exposure event.
// (gbCore is intentionally NOT window.sessionCore — that namespace is owned by session-manager.js
//  for Session Started, getMergedContext, etc. Merging would break session tracking.)
//
// Context flows in via window.__GBContext (server-rendered inline in theme.liquid).
// Anonymous ID is sourced from window.analytics (Segment) so warehouse joins between
// exposures and conversion events use the same identifier.
(function () {
  // Segment persists its anonymousId in localStorage ('ajs_anonymous_id', JSON-encoded) and a
  // cookie of the same name. These exist on a RETURNING visit BEFORE the analytics bundle finishes
  // loading, so we can bucket a returning visitor on their real anonymous_id without waiting for the
  // SDK — and returning visitors are exactly the population that produced the multiple-exposure 3%.
  function readPersistedAnonId() {
    var raw = lsGet('ajs_anonymous_id');
    if (raw) { try { return JSON.parse(raw); } catch (e) { return raw; } }
    return getCookie('ajs_anonymous_id');
  }

  function getAnonymousId() {
    try {
      if (window.analytics && typeof window.analytics.user === 'function') {
        var u = window.analytics.user();
        if (u && typeof u.anonymousId === 'function') {
          var id = u.anonymousId();
          if (id) return id;
        }
      }
    } catch (e) {}
    return readPersistedAnonId();
  }

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax';
  }

  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) {}
  }

  // Resolve the bucketing id (`gbuuid`) so it survives Safari/iOS ITP's ~7-day cap on
  // JavaScript-set (`document.cookie`) first-party cookies. gbuuid is the experiment hash
  // attribute, so if the cookie is silently expired/regenerated the visitor RE-BUCKETS — and a
  // single analysis unit (Segment anonymous_id, which lives in localStorage and outlives the
  // cookie) then accumulates `Experiment Viewed` events in BOTH arms → GrowthBook flags it as a
  // "multiple exposure" and drops the unit (the 3% we saw on the health tab). Fix: mirror gbuuid
  // in localStorage (not subject to the 7-day cookie cap) and restore the cookie from it, so the
  // SAME gbuuid → SAME bucket persists across the cookie's expiry.
  //
  // Priority: live cookie → localStorage mirror → freshly generated. We always re-assert BOTH on
  // every load: this restores a capped/cleared cookie from the mirror AND backfills the mirror for
  // existing cookie-only visitors — so the change is transition-safe (nobody currently holding a
  // gbuuid cookie gets re-bucketed on deploy; only cookies that were going to expire anyway are
  // now recoverable). gbuuid and anonymous_id both being localStorage-durable makes them 1:1 per
  // browser, which is what removes the multiple-exposure source.
  function resolveDurableGbuuid() {
    var fromCookie = getCookie('gbuuid');
    var fromLS = lsGet('gbuuid');
    var id = fromCookie || fromLS;
    if (!id) {
      id = (Date.now().toString(16)) + Math.random().toString(16).slice(2, 10);
    }
    setCookie('gbuuid', id, 365);
    if (fromLS !== id) lsSet('gbuuid', id);
    return id;
  }

  function resolveReady(value) {
    if (typeof window.__gbResolveReady === 'function') {
      window.__gbResolveReady(value);
      window.__gbResolveReady = null;
    } else {
      window.gbReady = Promise.resolve(value);
    }
  }

  function start() {
    var ctx = window.__GBContext || {};
    if (!ctx.clientKey) {
      return resolveReady(null);
    }

    // Maintain the durable gbuuid (cookie + localStorage mirror) on every load regardless — it is
    // the last-resort bucketing fallback and is also passed as a reporting attribute.
    var gbuuid = resolveDurableGbuuid();

    // Bucketing key (`id`, the experiment hashAttribute) is the Segment `anonymous_id` — the SAME
    // identifier GrowthBook analyzes/joins exposures on. Aligning assignment == analysis is what
    // eliminates the "multiple exposure" health warning: divergence between the bucketing id and
    // the analysis unit was splitting one user across both arms. anonymous_id is login-stable
    // (Segment keeps it across the multipass login reload, only adding userId on identify) and
    // localStorage-durable, so it survives ITP's 7-day cookie cap.
    //
    // To avoid a key FLIP (bucketing a user on gbuuid one visit and anonymous_id the next, which
    // would ITSELF create a multiple exposure — and is what made the header flip old↔new), we
    // resolve anonymous_id BEFORE initializing the SDK and prefer it as the key: returning visitors
    // resolve synchronously from Segment's persisted id; new visitors wait (up to the ceiling) for
    // Segment to generate it. Only after the wait ends, if it is still unavailable (Segment
    // blocked/down), do we fall back to the durable gbuuid so the visitor is still enrolled — a
    // consistently-blocked visitor stays consistently on gbuuid, so they don't flip; the residual
    // flip risk is just rare borderline-timing loads, which the wait minimizes. `customerId` is a
    // SEPARATE targeting/reporting attribute — never the bucketing key.
    var attempts = 0;
    // Ceiling for waiting on BOTH the SDK bundle AND the Segment anonymous_id. Sized to cover
    // Segment's async load on a cold first visit (~3s observed in the field, and its host is not
    // preconnected) so we bucket on anonymous_id and only fall back to gbuuid after this. This is a
    // background wait — the header has already rendered from the gate's cookie/fallback, so a longer
    // ceiling costs nothing visually. Tune DOWN to fall back to gbuuid sooner, UP if Segment is slower.
    var MAX_ATTEMPTS = 80; // ~4s ceiling (80 × 50ms)

    function sdkReady() {
      return !!(window.growthbook && window.growthbook.GrowthBook);
    }

    function tryInit() {
      var anonymousId = getAnonymousId();
      // Prefer the Segment anonymous_id as the bucketing key (it's also GrowthBook's analysis unit,
      // so assignment == analysis → no cross-arm split / no header flip). WAIT for BOTH the SDK
      // bundle AND the anonymous_id, up to the ceiling — the longer wait means the vast majority of
      // loads bucket on anonymous_id and the key stays stable. Only AFTER the wait ends do we fall
      // back to the durable gbuuid, so a visitor whose Segment never resolves (blocked/down) is
      // still enrolled instead of dropped. A consistently-blocked visitor sits consistently on
      // gbuuid (so they don't flip either); the flip risk is limited to rare borderline-timing
      // loads, which the wait minimizes.
      if (!sdkReady() || (!anonymousId && attempts < MAX_ATTEMPTS)) {
        if (++attempts > MAX_ATTEMPTS) {
          // SDK never attached → no GrowthBook at all (consumers show the theme default, no exposure).
          if (!sdkReady()) return resolveReady(null);
        }
        return setTimeout(tryInit, 50);
      }

      var bucketId = anonymousId || gbuuid; // gbuuid only as a last resort, after the wait ceiling
      var attributes = {
        id: bucketId,
        customerId: ctx.customerId || null,
        anonymousId: anonymousId || null,
        gbuuid: gbuuid,
        bucketed_on: anonymousId ? 'anonymous_id' : 'gbuuid', // lets you segment health by fallback rate
        user_type: ctx.userType || (ctx.customerId ? 'existing' : 'new'),
        url: location.href,
        path: location.pathname,
        pincode: getCookie('cpin') || null
      };

      try {
        var gb = new window.growthbook.GrowthBook({
          apiHost: 'https://cdn.growthbook.io',
          clientKey: ctx.clientKey,
          attributes: attributes,
          enableDevMode: !!ctx.devMode,
          subscribeToChanges: true,
          trackingCallback: function (experiment, result) {
            var payload = { experiment_id: experiment && experiment.key };
            if (result) {
              payload.variation_id = result.key;
              payload.variation_index = result.variationId;
              if (result.name != null) payload.variation_name = result.name;
              if (typeof result.inExperiment !== 'undefined') payload.in_experiment = result.inExperiment ? 1 : 0;
              if (result.hashAttribute != null) payload.hash_attribute = result.hashAttribute;
              if (result.hashValue != null) payload.hash_value = result.hashValue;
              if (typeof result.value !== 'undefined') payload.value = result.value;
            }
            // Sink 1: GTM dataLayer
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(Object.assign({ event: 'experiment_viewed' }, payload));
            // Sink 2: Segment → warehouse (joins exposures with conversions via Segment's anonymousId)
            try {
              if (window.analytics && typeof window.analytics.track === 'function') {
                window.analytics.track('Experiment Viewed', payload);
              }
            } catch (e) {}
          }
        });
        window.gb = gb;
        gb.loadFeatures().then(
          function () { resolveReady(gb); },
          function () { resolveReady(null); }
        );
      } catch (e) {
        resolveReady(null);
      }
    }
    tryInit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
